import { Label, Switch, TableRow, Textbox } from "@mcleod/components";

/**
 * Enum defining the supported field types for form field rendering
 */
export enum FormFieldType {
    CAPTION = "CAP",
    YES_NO = "YON",
    TEXTBOX = "default"
}

/**
 * Constants for form field rendering configuration
 */
export const FORM_FIELD_CONSTANTS = {
    WIDTH_MULTIPLIERS: {
        SMALL: 40,  // length < 2
        MEDIUM: 20, // length < 5
        LARGE: 8    // length >= 5
    },
    SWITCH_CAPTIONS: {
        LEFT: "No",
        RIGHT: "Yes"
    }
} as const;

/**
 * Interface defining the structure of form field data
 */
export interface FormFieldData {
    field_type: string;
    text?: string;
    x_placement: number;
    row_break?: boolean;
    length?: number;
}

/**
 * Configuration options for form field rendering
 */
export interface FormFieldRenderOptions {
    showFormDetailsLabel?: boolean;
    textboxWidth?: number;
}

/**
 * Handles the rendering of form fields into UI components.
 * Supports different field types including captions, yes/no switches, and textboxes.
 */
export class FormFieldRenderer {
    private componentIndex: number = 0;

    constructor(private readonly options: FormFieldRenderOptions = {}) {}

    /**
     * Renders form fields from an array of table rows into UI components
     * @param formFieldOptRows - Array of table rows containing form field data
     * @returns Array of rendered UI components
     */
    public renderFormFieldsFromTableRow(formFieldOptRows: TableRow[]): any[] {
        this.resetState();

        return formFieldOptRows
            .map(row => this.createComponentFromRow(row))
            .filter(component => component !== null);
    }

    /**
     * Resets the internal state for a new rendering cycle
     */
    private resetState(): void {
        this.componentIndex = 0;
    }

    /**
     * Creates a UI component from a table row
     * @param row - Table row containing form field data
     * @returns Rendered component or null if invalid
     */
    private createComponentFromRow(row: TableRow): any {
        const data = row.data.data as FormFieldData;
        return this.createComponent(data);
    }

    /**
     * Creates a UI component based on field type
     * @param data - Form field data
     * @returns Appropriate UI component based on field type
     */
    private createComponent(data: FormFieldData): any {
        switch (data.field_type) {
            case FormFieldType.CAPTION:
                return this.createCaptionComponent(data);
            case FormFieldType.YES_NO:
                return this.createSwitchComponent(data);
            default:
                return this.createTextboxComponent(data);
        }
    }

    /**
     * Creates a label component for caption fields
     * @param data - Form field data
     * @returns Label component
     */
    private createCaptionComponent(data: FormFieldData): Label {
        return new Label({
            text: data.text || "",
            id: this.generateComponentId("label", data.text),
            rowBreak: data.row_break || false
        });
    }

    /**
     * Creates a switch component for yes/no fields
     * @param data - Form field data
     * @returns Switch component
     */
    private createSwitchComponent(data: FormFieldData): Switch {
        const component = new Switch({
            caption: undefined, // Caption handling can be implemented if needed
            id: this.generateComponentId("switch"),
            enabled: false,
            rowBreak: data.row_break || false,
            leftCaption: FORM_FIELD_CONSTANTS.SWITCH_CAPTIONS.LEFT,
            rightCaption: FORM_FIELD_CONSTANTS.SWITCH_CAPTIONS.RIGHT
        });

        this.incrementIndex();
        return component;
    }

    /**
     * Creates a textbox component for input fields
     * @param data - Form field data
     * @returns Textbox component
     */
    private createTextboxComponent(data: FormFieldData): Textbox {
        const component = new Textbox({
            text: String(this.componentIndex),
            id: this.generateComponentId("textbox"),
            enabled: false,
            rowBreak: data.row_break || false,
            width: this.calculateTextboxWidth(data),
            captionVisible: false
        });

        this.incrementIndex();
        return component;
    }

    /**
     * Calculates the width for a textbox based on field length
     * @param data - Form field data
     * @returns Calculated width value
     */
    private calculateTextboxWidth(data: FormFieldData): number | undefined {
        if (!data.length) {
            return this.options.textboxWidth;
        }

        const { length } = data;
        const { WIDTH_MULTIPLIERS } = FORM_FIELD_CONSTANTS;

        if (length < 2) {
            return length * WIDTH_MULTIPLIERS.SMALL;
        } else if (length < 5) {
            return length * WIDTH_MULTIPLIERS.MEDIUM;
        } else {
            return length * WIDTH_MULTIPLIERS.LARGE;
        }
    }

    /**
     * Generates a unique component ID
     * @param type - Component type
     * @param suffix - Optional suffix for the ID
     * @returns Generated component ID
     */
    private generateComponentId(type: string, suffix?: string): string {
        const baseSuffix = suffix || String(this.componentIndex);
        return `${type}Field${baseSuffix}`;
    }

    /**
     * Increments the component index
     */
    private incrementIndex(): void {
        this.componentIndex++;
    }
}
